import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "../services/api";
import { Layers, FileText } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { slugify } from "../utils/slugify";


export default function Dashboard() {
  const [summary, setSummary] = useState([]);
  const [expandedDept, setExpandedDept] = useState(null);
  const [loading, setLoading] = useState(true);
  const containerRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchSummary() {
      try {
        const res = await axios.get("/authorized-reports/user");
        const grouped = Object.entries(res.data || {}).reduce((acc, [dept, reports]) => {
          acc[dept] = { count: reports.length, reports, recent: null };
          reports.forEach((report) => {
            const rawDate = report.created_at || report.updated_at || report.timestamp;
            const parsedDate = new Date(rawDate);
            if (!isNaN(parsedDate)) {
              const isoDate = parsedDate.toISOString().split("T")[0];
              if (!acc[dept].recent || parsedDate > new Date(acc[dept].recent)) {
                acc[dept].recent = isoDate;
              }
            }
          });
          return acc;
        }, {});
        setSummary(Object.entries(grouped));
      } catch (err) {
        console.error("Failed to fetch authorized report summary:", err);
      } finally {
        setLoading(false);
      }
    }

    fetchSummary();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setExpandedDept(null);
      }
    };

    if (expandedDept) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [expandedDept]);

  const chartData = summary.map(([department, data]) => ({
    name: department,
    Reports: data.count,
  }));

  const toggleExpand = (dept) => {
    setExpandedDept((prev) => (prev === dept ? null : dept));
  };

  return (
    <div className="flex flex-col h-full w-full overflow-hidden">
      <div className="flex-1 overflow-y-auto p-4 sm:p-6 pb-[10px]">
        <h2 className="text-3xl sm:text-4xl font-bold mb-8 text-center text-gray-800 flex items-center justify-center gap-2">
          <span role="img" aria-label="chart">📊</span> Report Summary
        </h2>

        {loading ? (
          <div className="text-center py-12 text-gray-400 animate-pulse text-lg">
            Loading dashboard...
          </div>
        ) : (
          <>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 mb-10" ref={containerRef}>
              {summary.map(([department, data], i) => (
                <motion.div
                  key={department}
                  className={`bg-gradient-to-br from-white to-blue-50 shadow-xl rounded-2xl p-6 border border-blue-100 hover:shadow-2xl transition-all cursor-pointer ${
                    expandedDept && expandedDept !== department ? "opacity-40" : ""
                  }`}
                  onClick={() => toggleExpand(department)}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  layout
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-lg sm:text-xl font-semibold text-gray-800">
                      {department}
                    </div>
                    <Layers className="w-6 h-6 text-blue-500" />
                  </div>
                  <div className="text-sm text-gray-500">Total Reports</div>
                  <div className="text-4xl font-bold text-blue-700">{data.count}</div>
                  <div className="text-xs text-gray-400 mt-1">
                    Last Updated: {data.recent || "N/A"}
                  </div>

                  {/* Expand section */}
                  <AnimatePresence>
                    {expandedDept === department && (
                      <motion.div
                        className="mt-5 space-y-2"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        {data.reports.slice(0, 6).map((report, idx) => (
                          <motion.div
                            key={report._id || idx}
                            className="bg-white rounded-xl shadow p-3 border flex items-center gap-3 hover:bg-blue-50 transition cursor-pointer"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: idx * 0.05 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              const path = `/reports/${slugify(department)}/${slugify(report.title)}`;
                              navigate(path);
                            }}
                          >
                            <FileText className="w-5 h-5 text-blue-400" />
                            <div className="text-sm text-gray-700 truncate">
                              {report.title || "Untitled Report"}
                            </div>
                          </motion.div>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>

            <div className="p-4 sm:p-6 bg-white rounded-2xl shadow border border-gray-100">
              <h3 className="text-xl font-semibold mb-4 text-gray-700 flex items-center gap-2">
                <span role="img" aria-label="bar">📈</span> Reports Per Department
              </h3>
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={chartData}
                    margin={{ top: 10, right: 30, left: 0, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#ffffff",
                        border: "1px solid #e5e7eb",
                        fontSize: "14px",
                      }}
                      labelStyle={{ fontWeight: "bold", color: "#4b5563" }}
                    />
                    <Bar dataKey="Reports" fill="#3b82f6" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center text-gray-400 py-8 italic">
                  No report data to display.
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
